import React, { useMemo } from 'react';
import createReducer from '../../../../../../questionnaire/reducer';
import { format, parse } from 'date-fns';
import styles from '../../styles';
import { useValue } from '../../ValueContext';
import createSelector from '../../../../../../questionnaire/selector';
import MedicalPassportField from '../../components/MedicalPassportField';
import toBMI, { calculateBMI } from '../../../../../../questionnaire/toBMI';
import toAge, { getAge } from '../../../../../../questionnaire/toAge';
import toSex from '../../../../../../questionnaire/toSex';
import MedicalPassportTextArea from '../../components/MedicalPassportTextArea';
import MedicalPassportCheckbox from '../../components/MedicalPassportCheckbox';
import toStringWithoutQuestions from '../../../../../../questionnaire/toStringWithoutQuestions';
import { capitalize } from 'lodash';

const showBMI = value => {
  const bmi = value?.demographicInformation?.bmi;
  const weightLb = bmi ? +bmi?.weightLb || 0 : 0;
  return weightLb >= 400;
};

const medicalHistoryReducer = createReducer({
  Sections: sections => null,
  Object: fields => {
    const visibleFields = fields.filter(field => field.child);

    return visibleFields.length === 0 ? null : visibleFields.length === 1 ? (
      visibleFields[0].child
    ) : (
      <ul>
        {visibleFields.map((field, i) => (
          <li key={i}>{field.child}</li>
        ))}
      </ul>
    );
  },
  Text: (label, text) =>
    text ? (
      <p>
        {label}: {text}
      </p>
    ) : null,
  LongText: (label, text) =>
    text ? (
      <p>
        {label}: {text}
      </p>
    ) : null,
  Checkboxes: (label, checkboxes) => {
    const visibleCheckboxes = checkboxes.filter(checkbox => checkbox.checked);
    return visibleCheckboxes.length === 0 ? null : visibleCheckboxes.length === 1 ? (
      <div>
        <div>{visibleCheckboxes[0].label}</div>
        {visibleCheckboxes[0].child && <div>{visibleCheckboxes[0].child}</div>}
      </div>
    ) : (
      <ul style={{ listStyleType: 'circle' }}>
        {visibleCheckboxes.map((checkbox, i) => (
          <li key={i}>
            <div>{checkbox.label}</div>
            {checkbox.child && <div>{checkbox.child}</div>}
          </li>
        ))}
      </ul>
    );
  },
  List: (label, children) => {
    const visibleChildren = children.filter(child => child);
    return visibleChildren.length === 0 ? null : visibleChildren.length === 1 ? (
      visibleChildren[0]
    ) : (
      <ul>
        {visibleChildren.map((child, i) => (
          <li key={i}>{child}</li>
        ))}
      </ul>
    );
  },
  YesNo: (question, positive, child) => {
    const transformedQuestion = capitalize(
      question
        .replace(/\?/g, '')
        .replace(/^Do you have\s/g, '')
        .replace(/^Are you\s/g, '')
    );
    return positive ? (
      <div>
        <div>{transformedQuestion}</div>
        {child ? <div>{child}</div> : null}
      </div>
    ) : null;
  },
  OneOf: (label, options, answer) =>
    answer ? (
      <div>
        <div>
          {label}: {answer.label}
        </div>
        {answer.child && <div>{answer.child}</div>}
      </div>
    ) : null,
  Medication: value => (value ? <span>{value}</span> : null),
  BodyMassIndex: (heightFt, heightIn, weightLb) => null,
  DateOfBirth: date => (date ? <span>{format(date, 'MM/DD/YYYY')}</span> : null),
  Date: date => (date ? <span>{format(date, 'MM/DD/YYYY')}</span> : null),
});

const medicalHistorySelector = createSelector(['medicalHistory']);

const Page1 = ({ hospitalName, defaultValue, lastPreOpCompletedEvent }) => {
  const { questionnaire, questionnaireAnswers } = useValue();

  const medicalHistory = useMemo(() => {
    const node = medicalHistorySelector(questionnaire, questionnaireAnswers);

    return (node && medicalHistoryReducer(...node)) || null;
  }, [questionnaire, questionnaireAnswers]);

  const age = getAge(parse(defaultValue?.demographicInformation?.dob, 'MM/DD/YYYY'));
  const bmi = calculateBMI(
    parseInt(defaultValue?.demographicInformation?.bmi?.weightLb, 10),
    parseInt(defaultValue?.demographicInformation?.bmi?.heightFt, 10),
    parseInt(defaultValue?.demographicInformation?.bmi?.heightIn, 10)
  );

  return (
    <div id="page1" className="page">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '50% 50%',
          gridGap: '1rem',
          alignContent: 'start',
          marginBottom: '1rem',
        }}
      >
        <div style={styles.div}>
          <h3>{(hospitalName || '').toUpperCase()}</h3>
          <MedicalPassportField name="procedure.physician.name" label="Surgeon" />
          <MedicalPassportField name="procedure.serviceTime" label="Procedure Date" />
          <MedicalPassportTextArea name="procedure.name" label="Procedure" style={{ display: 'flex' }} />
        </div>
        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
          <div></div>
          <div
            style={{
              border: '1px solid black',
              padding: '1rem',
              lineHeight: '25px',
            }}
          >
            <MedicalPassportField name="procedure.patient.name" label="Patient" fontWeight={'bold'} fontSize={16} />
            <MedicalPassportField name="procedure.patient.providerId" label="MRN#" />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '40% 60%',
                alignContent: 'start',
              }}
            >
              <MedicalPassportField name="dob" label="DOB" path={['demographicInformation', 'dob']} />
              <MedicalPassportField
                name="age"
                label="Age"
                path={['demographicInformation', 'dob']}
                reducer={toAge}
                warning={age > 70}
              />
              <MedicalPassportField name="procedure.serviceTime" label="DOS" />
              <MedicalPassportField name="sex" label="Sex" path={['demographicInformation', 'sex']} reducer={toSex} />
            </div>
            <MedicalPassportField name="procedure.physician.name" label="Surgeon" />
            <MedicalPassportField name="procedure.pcp" label="PCP" />
          </div>
        </div>
      </div>
      {lastPreOpCompletedEvent && (
        <div style={styles.div}>
          Pre-Op Performed By {lastPreOpCompletedEvent?.user?.name || ''}, Date{' '}
          {lastPreOpCompletedEvent?.timestamp ? format(lastPreOpCompletedEvent.timestamp, 'MM/DD/YYYY HH:mm') : ''}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '50% 50%',
          gridGap: '1rem',
          alignContent: 'start',
        }}
      >
        <div
          style={{
            gridColumn: '1 / span 2',
            height: '1px',
            background: 'black',
          }}
        />
        <div>
          <div style={{ fontWeight: 'bold' }}>CLINICALLY SIGNIFICANT ITEMS:</div>
          {showBMI(defaultValue) && (
            <MedicalPassportField
              name="bmi"
              label="BMI is"
              path={['demographicInformation', 'bmi']}
              reducer={toBMI}
              warning={bmi !== '-' ? bmi > 40 : false}
            />
          )}
          {medicalHistory}
        </div>
        <div style={styles.div}>
          <div style={{ fontWeight: 'bold' }}>PERSONAL INFORMATION:</div>
          <MedicalPassportField
            label="Primary Language"
            name="demographicInfoForm.primaryLanguage"
            path={['demographicInformation', 'primary language']}
            reducer={toStringWithoutQuestions}
          />
          <MedicalPassportCheckbox
            label="Okay to leave a message on machine."
            name="demographicInfoForm.okay.value"
            path={['demographicInformation', 'okay']}
          />
          <div>If not, where can we leave a message:</div>
          <MedicalPassportField
            name="demographicInfoForm.okay.message"
            path={['demographicInformation', 'okay', false, 'message']}
            reducer={toStringWithoutQuestions}
          />
          <div style={{ fontWeight: 'bold' }}>RESPONSIBLE PARTY:</div>
          <MedicalPassportField
            label="Name"
            name="contactInfoForm.responsibleParty.name"
            path={['contactinformation', 'responsibleparty', true, 'name']}
          />
          <MedicalPassportField
            label="Mobile Phone"
            name="contactInfoForm.responsibleParty.mobilePhone"
            path={['contactinformation', 'responsibleparty', true, 'Mobile Phone']}
          />
          <div style={{ fontWeight: 'bold' }}>RIDE HOME INFORMATION:</div>
          <MedicalPassportField
            label="Name"
            name="contactInfoForm.rideHomeInformation.name"
            path={['contactinformation', 'ridehomeinformation', true, 'name']}
          />
          <MedicalPassportField
            label="Relationship"
            name="contactInfoForm.rideHomeInformation.relationship"
            path={['contactinformation', 'ridehomeinformation', true, 'relationship']}
          />
          <MedicalPassportField
            label="Phone"
            name="contactInfoForm.rideHomeInformation.phone"
            path={['contactinformation', 'ridehomeinformation', true, 'phone']}
          />
          <MedicalPassportField
            label="Okay to share medical information with"
            name="contactInfoForm.rideHomeInformation.phone"
            path={['contactinformation', 'ridehomeinformation', true, 'share']}
          />
        </div>
      </div>
    </div>
  );
};

export default Page1;
